import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useStoreSettings } from '../../hooks/useStoreSettings';

export interface SeoHeadProps {
  /** Page-specific title; defaults to store_name. Final tag becomes "{title} | {store_name}". */
  title?: string;
  /** Manually written SEO title. Used verbatim, without the "| {store_name}" suffix. */
  titleOverride?: string | null;
  description?: string;
  /** Absolute or relative; relative is resolved against window.location.origin. */
  image?: string;
  /** Canonical URL; defaults to current location. */
  url?: string;
  /** "website" for landing/category pages, "product" for product detail. */
  type?: 'website' | 'product' | 'article';
  /** Set true to emit noindex/nofollow regardless of global default. */
  noindex?: boolean;
  /** Custom JSON-LD payload(s). Pass an array or single object. */
  jsonLd?: object | object[];
}

const stripHtml = (html: string | null | undefined): string =>
  (html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

const truncate = (text: string, max = 155): string =>
  text.length > max ? text.slice(0, max - 1).trimEnd() + '…' : text;

const absoluteUrl = (input: string | undefined): string | undefined => {
  if (!input) return undefined;
  if (/^https?:\/\//i.test(input)) return input;
  if (typeof window === 'undefined') return input;
  try {
    return new URL(input, window.location.origin).toString();
  } catch {
    return input;
  }
};

export const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  titleOverride,
  description,
  image,
  url,
  type = 'website',
  noindex = false,
  jsonLd,
}) => {
  const { settings } = useStoreSettings();
  const storeName = settings?.store_name?.trim() || 'Shop';
  const seoDefaults = settings as any;
  // Default Open Graph image = the tenant's logo. (We dropped the
  // separate seo_default_image field in favor of always using the logo.)
  const fallbackImage = (settings?.header_content as any)?.logo?.url;
  const fallbackDescription = seoDefaults?.seo_default_description as string | undefined;
  const jsonLdEnabled = seoDefaults?.seo_jsonld_enabled !== false;

  const finalTitle =
    titleOverride?.trim() || (title ? `${title} | ${storeName}` : storeName);
  const finalDescription = truncate(stripHtml(description) || fallbackDescription || storeName);
  const finalUrl =
    absoluteUrl(url) ||
    (typeof window !== 'undefined' ? window.location.href : undefined);
  const finalImage = absoluteUrl(image) || absoluteUrl(fallbackImage);

  const robots = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
  const ldPayload = jsonLdEnabled
    ? Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []
    : [];

  const lang = (settings as any)?.language || 'sr-RS';

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="robots" content={robots} />
      {finalUrl && <link rel="canonical" href={finalUrl} />}

      <meta property="og:site_name" content={storeName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      {finalUrl && <meta property="og:url" content={finalUrl} />}
      {finalImage && <meta property="og:image" content={finalImage} />}

      <meta name="twitter:card" content={finalImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {finalImage && <meta name="twitter:image" content={finalImage} />}

      {ldPayload.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
